import React, { useRef } from 'react';
import { connect } from 'react-redux';
import classNames from 'classnames';

import { FormattedMessage, useIntl } from '../../util/reactIntl';
import { isScrollingDisabled } from '../../ducks/ui.duck';

import { ExternalLink, LayoutSingleColumn, Page } from '../../components';

import TopbarContainer from '../TopbarContainer/TopbarContainer';
import FooterContainer from '../FooterContainer/FooterContainer';

import sharedCss from '../_marketingPage/marketingPage.module.css';
import css from './CookiesPage.module.css';

/**
 * The set of cookies disclosed in the table. Each row points at
 * translation keys for `name`, `provider`, `purpose`, `type`,
 * and `duration` (e.g. `CookiesPage.tableRow1Name`).
 *
 * Edits to the cookie inventory belong here + in the translations,
 * not in the JSX below.
 */
const COOKIE_ROWS = [
  { key: 'row1' },
  { key: 'row2' },
  { key: 'row3' },
  { key: 'row4' },
  { key: 'row5' },
  { key: 'row6' },
];

/**
 * Cookies / "Informasjonskapsler" page — `/cookies`.
 *
 * Cookie disclosure (supplement to the privacy policy). Mirrors the
 * legal-corner chrome (PrivacyPolicy / Buy/Sell Terms) and adds:
 * - "You are in control" consent callout above the cookie table
 * - Responsive cookie table with zebra striping
 * - Cookie-card wrappers for instructional sections
 *
 * @param {Object} props
 * @param {boolean} props.scrollingDisabled
 * @returns {JSX.Element}
 */
const CookiesPageComponent = props => {
  const intl = useIntl();
  const { scrollingDisabled } = props;

  const supportEmail = 'post@zuprize.no';
  const strong = chunks => <strong>{chunks}</strong>;
  const br = <br />;
  const emailLink = (
    <ExternalLink href={`mailto:${supportEmail}`} className={sharedCss.kontaktLink}>
      {supportEmail}
    </ExternalLink>
  );

  // No cookie-consent modal is wired up yet; the "Change my cookie
  // preferences" CTA scrolls to the section that explains the manual
  // controls (browser settings + opt-out links). Swap to opening the
  // consent modal here once one ships.
  const controlsRef = useRef(null);
  const handleOpenConsent = () => {
    controlsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <Page
      title={intl.formatMessage({ id: 'CookiesPage.metaTitle' })}
      description={intl.formatMessage({ id: 'CookiesPage.metaDescription' })}
      scrollingDisabled={scrollingDisabled}
    >
      <LayoutSingleColumn topbar={<TopbarContainer />} footer={<FooterContainer />}>
        <article className={sharedCss.root}>
          <div className={sharedCss.content}>
            <header className={sharedCss.hero}>
              <p className={sharedCss.heroEyebrow}>
                <FormattedMessage id="CookiesPage.heroEyebrow" />
              </p>
              <h1 className={sharedCss.heroHeading}>
                <FormattedMessage id="CookiesPage.heroHeading" />
              </h1>
              <p className={sharedCss.lede}>
                <FormattedMessage id="CookiesPage.heroLede" />
              </p>
              <p className={sharedCss.body}>
                <FormattedMessage id="CookiesPage.heroSub" />
              </p>
              <p className={sharedCss.lastUpdated}>
                <FormattedMessage id="CookiesPage.lastUpdated" />
              </p>
            </header>

            <section className={sharedCss.section}>
              <p className={sharedCss.sectionEyebrow}>
                <FormattedMessage id="CookiesPage.s1Eyebrow" />
              </p>
              <p className={sharedCss.body}>
                <FormattedMessage id="CookiesPage.s1Body1" />
              </p>
              <p className={sharedCss.body}>
                <FormattedMessage id="CookiesPage.s1Body2" />
              </p>
            </section>

            <section className={sharedCss.section}>
              <p className={sharedCss.sectionEyebrow}>
                <FormattedMessage id="CookiesPage.s2Eyebrow" />
              </p>
              <p className={sharedCss.body}>
                <FormattedMessage id="CookiesPage.s2Body1" />
              </p>

              <p className={sharedCss.subHeading}>
                <FormattedMessage id="CookiesPage.s2Sub1" />
              </p>
              <p className={sharedCss.body}>
                <FormattedMessage id="CookiesPage.s2Sub1Body" />
              </p>

              <p className={sharedCss.subHeading}>
                <FormattedMessage id="CookiesPage.s2Sub2" />
              </p>
              <p className={sharedCss.body}>
                <FormattedMessage id="CookiesPage.s2Sub2Body" />
              </p>

              <p className={sharedCss.subHeading}>
                <FormattedMessage id="CookiesPage.s2Sub3" />
              </p>
              <p className={sharedCss.body}>
                <FormattedMessage id="CookiesPage.s2Sub3Body" />
              </p>

              <p className={sharedCss.subHeading}>
                <FormattedMessage id="CookiesPage.s2Sub4" />
              </p>
              <p className={sharedCss.body}>
                <FormattedMessage id="CookiesPage.s2Sub4Body" />
              </p>
            </section>

            <section className={sharedCss.section}>
              <p className={sharedCss.sectionEyebrow}>
                <FormattedMessage id="CookiesPage.s3Eyebrow" />
              </p>
              <p className={sharedCss.body}>
                <FormattedMessage id="CookiesPage.s3Body1" />
              </p>
              <p className={sharedCss.body}>
                <FormattedMessage id="CookiesPage.s3Body2" />
              </p>

              <div className={css.consentCallout}>
                <p className={css.consentCalloutEyebrow}>
                  <FormattedMessage id="CookiesPage.consentCalloutEyebrow" />
                </p>
                <p className={css.consentCalloutBody}>
                  <FormattedMessage id="CookiesPage.consentCalloutBody" />
                </p>
                <button
                  type="button"
                  className={css.consentCalloutButton}
                  onClick={handleOpenConsent}
                >
                  <FormattedMessage id="CookiesPage.consentCalloutCta" />
                </button>
              </div>

              <div className={css.tableWrapper}>
                <table className={css.table}>
                  <thead>
                    <tr>
                      <th scope="col">
                        <FormattedMessage id="CookiesPage.tableHeaderName" />
                      </th>
                      <th scope="col">
                        <FormattedMessage id="CookiesPage.tableHeaderProvider" />
                      </th>
                      <th scope="col" className={css.colPurpose}>
                        <FormattedMessage id="CookiesPage.tableHeaderPurpose" />
                      </th>
                      <th scope="col">
                        <FormattedMessage id="CookiesPage.tableHeaderType" />
                      </th>
                      <th scope="col">
                        <FormattedMessage id="CookiesPage.tableHeaderDuration" />
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {COOKIE_ROWS.map(({ key }) => (
                      <tr key={key}>
                        <td>
                          <code>
                            <FormattedMessage id={`CookiesPage.table${capitalize(key)}Name`} />
                          </code>
                        </td>
                        <td>
                          <FormattedMessage id={`CookiesPage.table${capitalize(key)}Provider`} />
                        </td>
                        <td className={css.colPurpose}>
                          <FormattedMessage id={`CookiesPage.table${capitalize(key)}Purpose`} />
                        </td>
                        <td>
                          <FormattedMessage id={`CookiesPage.table${capitalize(key)}Type`} />
                        </td>
                        <td>
                          <FormattedMessage id={`CookiesPage.table${capitalize(key)}Duration`} />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <p className={sharedCss.body}>
                <FormattedMessage id="CookiesPage.s3Body3" />
              </p>
            </section>

            <section className={sharedCss.section}>
              <p className={sharedCss.sectionEyebrow}>
                <FormattedMessage id="CookiesPage.s4Eyebrow" />
              </p>
              <p className={sharedCss.body}>
                <FormattedMessage id="CookiesPage.s4Body1" />
              </p>
              <p className={sharedCss.body}>
                <FormattedMessage id="CookiesPage.s4Body2" />
              </p>
              <p className={sharedCss.body}>
                <FormattedMessage id="CookiesPage.s4Body3" />
              </p>
            </section>

            <section className={sharedCss.section}>
              <p className={sharedCss.sectionEyebrow}>
                <FormattedMessage id="CookiesPage.s5Eyebrow" />
              </p>
              <p className={sharedCss.body}>
                <FormattedMessage id="CookiesPage.s5Body1" />
              </p>
              <p className={sharedCss.body}>
                <FormattedMessage id="CookiesPage.s5Body2" />
              </p>
              <p className={sharedCss.body}>
                <FormattedMessage id="CookiesPage.s5Body3" />
              </p>
              <ul className={sharedCss.list}>
                <li>
                  <ExternalLink
                    href="https://policies.google.com/privacy"
                    className={sharedCss.kontaktLink}
                  >
                    <FormattedMessage id="CookiesPage.s5Link1Text" />
                  </ExternalLink>
                </li>
                <li>
                  <ExternalLink
                    href="https://facebook.com/privacy/policy"
                    className={sharedCss.kontaktLink}
                  >
                    <FormattedMessage id="CookiesPage.s5Link2Text" />
                  </ExternalLink>
                </li>
              </ul>
            </section>

            <section className={sharedCss.section} ref={controlsRef}>
              <p className={sharedCss.sectionEyebrow}>
                <FormattedMessage id="CookiesPage.s6Eyebrow" />
              </p>
              <p className={sharedCss.body}>
                <FormattedMessage id="CookiesPage.s6Body1" />
              </p>

              <div className={css.cookieCard}>
                <p
                  className={classNames(sharedCss.subHeading, css.cardHeading)}
                >
                  <FormattedMessage id="CookiesPage.s6Sub1" />
                </p>
                <p className={sharedCss.body}>
                  <FormattedMessage id="CookiesPage.s6Sub1Body" />
                </p>
                {/* Re-opens Cookiebot's consent dialog so the user can
                    change their selections any time. The page-level
                    button replaces Cookiebot's own floating widget
                    (which collided with the mobile bottom nav). The
                    `typeof window` guard keeps SSR happy. */}
                <button
                  type="button"
                  className={css.openConsentButton}
                  onClick={() => {
                    if (typeof window !== 'undefined' && window.Cookiebot) {
                      window.Cookiebot.renew();
                    }
                  }}
                >
                  <FormattedMessage id="CookiesPage.openConsentDialog" />
                </button>
              </div>

              <div className={css.cookieCard}>
                <p
                  className={classNames(sharedCss.subHeading, css.cardHeading)}
                >
                  <FormattedMessage id="CookiesPage.s6Sub2" />
                </p>
                <p className={sharedCss.body}>
                  <FormattedMessage id="CookiesPage.s6Sub2Body" />
                </p>
                <p className={sharedCss.body}>
                  <ExternalLink
                    href="https://nettvett.no/slik-administrerer-du-informasjonskapsler/"
                    className={sharedCss.kontaktLink}
                  >
                    <FormattedMessage id="CookiesPage.s6NettvettLinkText" />
                  </ExternalLink>
                </p>
              </div>

              <div className={css.cookieCard}>
                <p
                  className={classNames(sharedCss.subHeading, css.cardHeading)}
                >
                  <FormattedMessage id="CookiesPage.s6Sub3" />
                </p>
                <p className={sharedCss.body}>
                  <FormattedMessage id="CookiesPage.s6Sub3Body" />
                </p>
                <ul className={sharedCss.list}>
                  <li>
                    <ExternalLink
                      href="https://youronlinechoices.eu"
                      className={sharedCss.kontaktLink}
                    >
                      <FormattedMessage id="CookiesPage.s6YourOnlineChoicesText" />
                    </ExternalLink>
                  </li>
                  <li>
                    <ExternalLink
                      href="https://aboutads.info"
                      className={sharedCss.kontaktLink}
                    >
                      <FormattedMessage id="CookiesPage.s6AboutAdsText" />
                    </ExternalLink>
                  </li>
                </ul>
              </div>
            </section>

            <section className={sharedCss.section}>
              <p className={sharedCss.sectionEyebrow}>
                <FormattedMessage id="CookiesPage.s7Eyebrow" />
              </p>
              <p className={sharedCss.body}>
                <FormattedMessage id="CookiesPage.s7Body1" />
              </p>
              <p className={sharedCss.body}>
                <FormattedMessage id="CookiesPage.s7Body2" />
              </p>
            </section>
          </div>

          <section className={sharedCss.kontakt}>
            <div className={sharedCss.kontaktInner}>
              <p className={sharedCss.kontaktEyebrow}>
                <FormattedMessage id="CookiesPage.kontaktEyebrow" />
              </p>
              <p className={sharedCss.kontaktBody}>
                <FormattedMessage
                  id="CookiesPage.kontaktBody"
                  values={{ br, email: emailLink }}
                />
              </p>
            </div>
          </section>
        </article>
      </LayoutSingleColumn>
    </Page>
  );
};

const capitalize = str => str.charAt(0).toUpperCase() + str.slice(1);

const mapStateToProps = state => ({
  scrollingDisabled: isScrollingDisabled(state),
});

const CookiesPage = connect(mapStateToProps)(CookiesPageComponent);

export { CookiesPageComponent };
export default CookiesPage;
